import React, { ReactElement, useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
// @ts-ignore
import Context from "@fdr-mar/promos-context";
import { format } from "date-fns";
import { AlertInLine, AlertInlineVariants } from "@tvg/design-system/web";
import {
  breakpoints,
  useMediaQuery,
  ToastManager,
  bottomOffsets
} from "@tvg/design-system";
import { getPromoCardsModalOpen } from "@urp/promos-page/redux/selectors";
import { getIsLogged } from "@urp/store-selectors";
import { getOptedInMessage } from "../../redux/selectors";
import { Blok, ErrorMessages } from "../../types";
import LegalContent from "../PromosLegalContent";
import TableContent from "../TableComponent";
import PromosInfoContent from "../PromosInfoContent";
import ContentText from "../ContentText";
import ContentTitle from "../ContentTitle";
import HandicappingSection from "../HandicappingSection";
import EligibleRaces from "../EligibleRaces";
import {
  PromosBodyContentContainer,
  PromosBodyContentWrapper,
  ContentContainer,
  LeftColumn,
  RightColumn,
  FullRow
} from "./styled-components";

const { OptinContext } = Context;

interface Content {
  bloks: Blok[];
}

interface Props {
  content: Content;
}

const COMPONENTS_DICTIONARY: {
  [key: string]: ({ content }: Props) => JSX.Element;
} = {
  // @ts-ignore
  legal_content: LegalContent,
  // @ts-ignore
  table_content: TableContent,
  // @ts-ignore
  info_section: PromosInfoContent,
  // @ts-ignore
  content_title: ContentTitle,
  // @ts-ignore
  content_text: ContentText,
  // @ts-ignore
  handicapping: HandicappingSection,
  // @ts-ignore
  eligible_races_section: EligibleRaces
};

/* eslint-disable no-underscore-dangle */

export const renderChildrenComponents = (
  bloks: Blok[],
  isMobile: boolean
): { left: ReactElement[]; right: ReactElement[]; fullRow: ReactElement[] } =>
  bloks.reduce<{
    left: ReactElement[];
    right: ReactElement[];
    fullRow: ReactElement[];
  }>(
    (acc, blok) => {
      if (isMobile && blok.component === "handicapping") {
        return acc;
      }

      if (COMPONENTS_DICTIONARY[blok.component]) {
        const componentElement = React.createElement(
          COMPONENTS_DICTIONARY[blok.component],
          {
            key: blok._uid,
            content: { bloks: [blok] }
          }
        );

        if (blok.component === "legal_content") {
          acc.fullRow.push(componentElement);
        } else {
          const isLeftColumn = [
            "content_title",
            "content_text",
            "eligible_races_section"
          ].includes(blok.component);
          if (isLeftColumn) {
            acc.left.push(componentElement);
          } else {
            acc.right.push(componentElement);
          }
        }
      }
      return acc;
    },
    { left: [], right: [], fullRow: [] }
  );

export const checkPromoStartDate = (blok?: Blok[]) => {
  const dateBlok = blok?.find((b) => b.component === "content_date");

  if (!dateBlok) {
    return null;
  }

  const { date } = dateBlok;
  const parsedDate = new Date(date as Date);
  const finalDate = format(parsedDate, "EEEE, MMM d 'at' haaa");

  return date && parsedDate >= new Date() ? finalDate : null;
};

const PromosBodyContent = ({ content }: Props) => {
  const optedInMessage = useSelector(getOptedInMessage);
  const isPromoCardsModalOpen = useSelector(getPromoCardsModalOpen);
  const isLogged = useSelector(getIsLogged);
  const location = useLocation();
  const optinContext: Context = useContext(OptinContext);
  const isMobile = useMediaQuery(breakpoints.desktop.max.sm);
  const { left, right, fullRow } = renderChildrenComponents(
    content.bloks,
    isMobile
  );
  const [shouldShowToast, setShouldShowToast] = useState(false);
  const [messageOptedIn, setMessageOptedIn] = useState("");
  const [toastContent, setToastContent] = useState<{
    msg: string;
    type: AlertInlineVariants;
    dismiss: boolean;
    label?: string;
  }>({
    msg: "",
    type: "informational" as AlertInlineVariants,
    dismiss: false
  });

  const hasPromoDate = {
    hasDate: false,
    date: ""
  };

  useEffect(() => {
    if (
      (isMobile && location.search.includes("promo")) ||
      (isMobile && isPromoCardsModalOpen)
    ) {
      setShouldShowToast(true);
    } else if (isMobile && !isPromoCardsModalOpen) {
      setShouldShowToast(false);
    }

    setMessageOptedIn(
      optinContext.isOpted && optinContext.optedIn
        ? optedInMessage?.success?.successOptedIn?.text
        : optedInMessage?.success?.alreadyOptedIn?.text
    );

    if (optinContext.error) {
      setErrorMessage(optinContext.error);
    }
  }, [optinContext]);

  useEffect(() => {
    if (isPromoCardsModalOpen) {
      setShouldShowToast(true);
    } else {
      setShouldShowToast(false);
      ToastManager.hide();
    }
  }, [isPromoCardsModalOpen]);

  useEffect(() => {
    if (shouldShowToast) {
      if (optinContext.error)
        renderMobileToast(toastContent.msg, toastContent.type, false);

      if (optinContext.optedIn || optinContext.isOpted)
        renderMobileToast(
          messageOptedIn,
          optedInMessage.success.alreadyOptedIn.variant,
          true,
          "optedinMessage"
        );

      if (hasPromoDate.hasDate)
        renderMobileToast(
          `${optedInMessage.info.startDate.text} ${hasPromoDate.date}`,
          optedInMessage.info.startDate.variant,
          false,
          "optedinMessage"
        );
    } else {
      ToastManager.hide();
    }
  }, [shouldShowToast, optinContext, isLogged]);

  const renderMobileToast = (
    msg: string,
    type: AlertInlineVariants,
    dismiss: boolean,
    label?: string
  ) => {
    ToastManager.show({
      message: msg,
      variant: type,
      isFullWidth: true,
      hasDismissButton: dismiss,
      autoHide: false,
      bottomOffset: bottomOffsets.TALL + 12,
      hasRoundedStyle: false,
      qaLabel: label || `${type}-message`,
      onDismissPress: () => {
        ToastManager.hide();
        setShouldShowToast(false);
      }
    });
  };

  const setErrorMessage = (error: string) => {
    const errorMapping: {
      [key: string]: { text: string; variant: AlertInlineVariants };
    } = {
      PromotionNotFoundException: optedInMessage.info.notFound,
      PromotionNotAvailableForOptin: optedInMessage.error.notFound,
      NotEligibleException: optedInMessage.warning.notEligible,
      UserOnBlackListException: optedInMessage.warning.userOnBlackList,
      NotInValidOptinRangeException: optedInMessage.info.invalidOptinRange,
      InternalError: optedInMessage.error.internalError,
      default: optedInMessage.error.default
    };

    const { text, variant } =
      errorMapping[error as keyof ErrorMessages] || errorMapping.default;

    setToastContent({
      msg: text,
      type: variant,
      dismiss: false
    });
  };

  const renderStartDateAlert = (blok: Blok[]) => {
    const date = checkPromoStartDate(blok);

    if (date) {
      hasPromoDate.hasDate = true;
      hasPromoDate.date = date;
      return (
        !isMobile && (
          <AlertInLine
            type="fixed"
            qaLabel="optedinMessage"
            color="--fd-colors-content-strong"
            message={`${optedInMessage.info.startDate.text} ${date}`}
            variant={optedInMessage.info.startDate.variant}
          />
        )
      );
    }
    return null;
  };

  return (
    <PromosBodyContentContainer
      data-qa-label="promosBodyContentContainer"
      isMobile={isMobile}
    >
      <PromosBodyContentWrapper
        data-qa-label="promosBodyContentWrapper"
        isMobile={isMobile}
      >
        {!isMobile && (
          <>
            {optinContext.error && optedInMessage && (
              <AlertInLine
                type="fixed"
                qaLabel={`${toastContent.msg}-message`}
                message={toastContent.msg}
                variant={toastContent.type}
              />
            )}
            {(optinContext.optedIn || optinContext.isOpted) &&
              optedInMessage && (
                <AlertInLine
                  type="fixed"
                  qaLabel="optedinMessage"
                  color="--fd-colors-content-strong"
                  message={messageOptedIn}
                  variant={optedInMessage.success.alreadyOptedIn.variant}
                  hasDismissButton
                />
              )}
          </>
        )}
        {optedInMessage && renderStartDateAlert(content.bloks)}
        <ContentContainer isMobile={isMobile}>
          <LeftColumn isMobile={isMobile}>{left}</LeftColumn>
          <RightColumn isMobile={isMobile}>{right}</RightColumn>
        </ContentContainer>
        {fullRow.map((component) => (
          <FullRow key={component.key}>{component}</FullRow>
        ))}
      </PromosBodyContentWrapper>
    </PromosBodyContentContainer>
  );
};

export default PromosBodyContent;
